import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import creditService from "services/credits.js";

const PERIOD_OPTIONS = [
  { label: "7 días", value: 7 },
  { label: "30 días", value: 30 },
  { label: "90 días", value: 90 },
];

const TYPE_LABELS = {
  agent_execution: "Ejecución de agente",
  plan_allocation: "Asignación de plan",
  manual_adjustment: "Ajuste manual",
};

function StatCard({ icon, iconBg, iconColor, label, value, sub }) {
  return (
    <div className="bg-white rounded-lg shadow-md p-5 flex items-center gap-4 min-w-0 flex-1">
      <div className={`flex-shrink-0 w-12 h-12 rounded-full ${iconBg} flex items-center justify-center`}>
        <i className={`fas ${icon} ${iconColor} text-xl`}></i>
      </div>
      <div>
        <p className="text-xs font-bold text-blueGray-400 uppercase tracking-wide mb-0.5">{label}</p>
        <p className="text-2xl font-bold text-blueGray-700">{value}</p>
        {sub && <p className="text-xs text-blueGray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function ProgressBar({ value, max, colorClass = "bg-lightBlue-400" }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="w-full bg-blueGray-200 rounded-full h-1.5 mt-1">
      <div className={`h-1.5 rounded-full ${colorClass}`} style={{ width: `${pct}%` }}></div>
    </div>
  );
}

function ProjectUsersModal({ projectCode, projectName, period, onClose }) {
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    creditService.getProjectUsers(projectCode, { period })
      .then((res) => setUsers(res.data?.users || []))
      .catch(() => setError("No se pudieron cargar los usuarios del proyecto."))
      .finally(() => setLoading(false));
    return () => { document.body.style.overflow = "unset"; };
  }, [projectCode, period]);

  const maxConsumed = users?.length ? Math.max(...users.map((u) => Number(u.total_consumed))) : 1;

  return createPortal(
    <div
      style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)", zIndex: 99999,
        display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem",
      }}
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-blueGray-100">
          <div>
            <h3 className="text-base font-bold text-blueGray-700 flex items-center gap-2">
              <i className="fas fa-folder-open text-lightBlue-400"></i>
              {projectName}
            </h3>
            <p className="text-xs text-blueGray-400 mt-0.5">
              Top consumidores de créditos — últimos {period} días
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-blueGray-400 hover:text-blueGray-600 transition-colors"
          >
            <i className="fas fa-times text-lg"></i>
          </button>
        </div>

        <div className="p-5">
          {loading && (
            <div className="flex justify-center items-center py-10 text-blueGray-400">
              <i className="fas fa-spinner fa-spin mr-2"></i>
              <span className="text-sm">Cargando...</span>
            </div>
          )}
          {error && (
            <p className="text-sm text-red-500 text-center py-6">
              <i className="fas fa-exclamation-triangle mr-2"></i>{error}
            </p>
          )}
          {users && users.length === 0 && (
            <p className="text-xs text-blueGray-400 text-center py-8">
              Sin consumo registrado en este proyecto para el período.
            </p>
          )}
          {users && users.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-blueGray-100">
                    <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase pr-4">#</th>
                    <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase pr-4">Usuario</th>
                    <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase pr-4">Plan</th>
                    <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase pr-4">Consumido</th>
                    <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase">Transacciones</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, idx) => (
                    <tr key={user.user_id} className="border-b border-blueGray-50 hover:bg-blueGray-50 transition-colors">
                      <td className="py-2 pr-4 text-blueGray-400 font-bold text-xs">{idx + 1}</td>
                      <td className="py-2 pr-4">
                        <p className="font-semibold text-blueGray-700 text-xs">{user.user_name}</p>
                        <p className="text-blueGray-400 text-xs">{user.user_email}</p>
                      </td>
                      <td className="py-2 pr-4">
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-600">
                          {user.plan_name ?? "Sin plan"}
                        </span>
                      </td>
                      <td className="py-2 pr-4">
                        <p className="font-bold text-orange-500 text-sm">
                          {Number(user.total_consumed).toLocaleString()}
                        </p>
                        <ProgressBar value={Number(user.total_consumed)} max={maxConsumed} colorClass="bg-orange-300" />
                      </td>
                      <td className="py-2 text-blueGray-600 text-xs">{user.transaction_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function CreditAnalytics() {
  const [period, setPeriod] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await creditService.getAnalytics({ period });
      setData(res.data);
    } catch (e) {
      setError("No se pudo cargar la información de analíticas.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const s = data?.summary || {};
  const maxConsumed = data?.top_consumers?.length
    ? Math.max(...data.top_consumers.map((u) => u.total_consumed))
    : 1;
  const maxDaily = data?.daily_trends?.length
    ? Math.max(...data.daily_trends.map((d) => d.consumed))
    : 1;

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-4 mt-4 mb-4">
        <div>
          <h2 className="text-xl font-bold text-blueGray-700">Analíticas de Créditos</h2>
          <p className="text-xs text-blueGray-400 mt-0.5">
            Visibilidad sobre el consumo de créditos en la plataforma
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-blueGray-200 overflow-hidden">
            {PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setPeriod(opt.value)}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                  period === opt.value
                    ? "bg-lightBlue-500 text-white"
                    : "bg-white text-blueGray-500 hover:bg-blueGray-50"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            onClick={loadAnalytics}
            disabled={loading}
            className="flex items-center gap-1.5 text-xs text-lightBlue-500 hover:text-lightBlue-700 font-semibold disabled:opacity-50 transition-colors ml-2"
            title="Actualizar"
          >
            <i className={`fas fa-sync-alt text-xs ${loading ? "fa-spin" : ""}`}></i>
            {loading ? "Cargando..." : "Actualizar"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-4 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          <i className="fas fa-exclamation-triangle mr-2"></i>{error}
        </div>
      )}

      {loading && !data && (
        <div className="flex justify-center items-center py-20 text-blueGray-400">
          <i className="fas fa-spinner fa-spin text-2xl mr-3"></i>
          <span className="text-sm">Cargando analíticas...</span>
        </div>
      )}

      {data && (
        <>
          {/* Summary Cards */}
          <div className="flex flex-wrap px-4 gap-4 mb-6">
            <StatCard
              icon="fa-fire"
              iconBg="bg-orange-100"
              iconColor="text-orange-500"
              label={`Consumido (${period}d)`}
              value={s.period_consumed?.toLocaleString() ?? "—"}
              sub={`Total histórico: ${s.total_consumed_all_time?.toLocaleString()}`}
            />
            <StatCard
              icon="fa-coins"
              iconBg="bg-lightBlue-100"
              iconColor="text-lightBlue-500"
              label={`Asignado (${period}d)`}
              value={s.period_allocated?.toLocaleString() ?? "—"}
              sub={`Total histórico: ${s.total_allocated_all_time?.toLocaleString()}`}
            />
            <StatCard
              icon="fa-users"
              iconBg="bg-indigo-100"
              iconColor="text-indigo-500"
              label="Usuarios activos"
              value={s.active_users_period ?? "—"}
              sub={`de ${s.total_users} totales`}
            />
            <StatCard
              icon="fa-chart-line"
              iconBg="bg-emerald-100"
              iconColor="text-emerald-500"
              label="Promedio / usuario"
              value={s.avg_consumed_per_user?.toLocaleString() ?? "—"}
              sub={`créditos en ${period} días`}
            />
            {s.users_near_threshold > 0 && (
              <StatCard
                icon="fa-exclamation-triangle"
                iconBg="bg-red-100"
                iconColor="text-red-500"
                label="Usuarios en alerta"
                value={s.users_near_threshold}
                sub="Con < 20 % de créditos restantes"
              />
            )}
          </div>

          <div className="flex flex-wrap px-4 gap-6 mb-6">
            {/* Tendencia diaria */}
            <div className="bg-white rounded-lg shadow-md p-5 flex-1 min-w-64">
              <h3 className="text-sm font-bold text-blueGray-600 mb-4 flex items-center gap-2">
                <i className="fas fa-chart-bar text-lightBlue-400"></i>
                Tendencia diaria de consumo
              </h3>
              {data.daily_trends?.length === 0 ? (
                <p className="text-xs text-blueGray-400 text-center py-4">Sin datos en el período</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {[...data.daily_trends].reverse().map((day) => (
                    <div key={day.date}>
                      <div className="flex justify-between text-xs text-blueGray-500 mb-0.5">
                        <span>{day.date}</span>
                        <span className="font-semibold text-orange-500">
                          {Number(day.consumed).toLocaleString()} consumidos
                        </span>
                      </div>
                      <ProgressBar value={Number(day.consumed)} max={maxDaily} colorClass="bg-orange-400" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Consumo por tipo */}
            <div className="bg-white rounded-lg shadow-md p-5 w-72">
              <h3 className="text-sm font-bold text-blueGray-600 mb-4 flex items-center gap-2">
                <i className="fas fa-tag text-indigo-400"></i>
                Consumo por tipo
              </h3>
              {data.consumption_by_type?.length === 0 ? (
                <p className="text-xs text-blueGray-400 text-center py-4">Sin datos en el período</p>
              ) : (
                <div className="space-y-3">
                  {data.consumption_by_type.map((item) => (
                    <div key={item.reference_type ?? "null"}>
                      <div className="flex justify-between text-xs text-blueGray-500 mb-0.5">
                        <span className="font-medium">
                          {TYPE_LABELS[item.reference_type] ?? item.reference_type ?? "Sin tipo"}
                        </span>
                        <span className="font-bold text-blueGray-700">
                          {Number(item.total).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-xs text-blueGray-400">{item.count} transacciones</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Top consumidores */}
          <div className="px-4 mb-6">
            <div className="bg-white rounded-lg shadow-md p-5">
              <h3 className="text-sm font-bold text-blueGray-600 mb-4 flex items-center gap-2">
                <i className="fas fa-trophy text-yellow-400"></i>
                Top consumidores ({period} días)
              </h3>
              {data.top_consumers?.length === 0 ? (
                <p className="text-xs text-blueGray-400 text-center py-4">Sin consumo en el período</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-blueGray-100">
                        <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase pr-4">#</th>
                        <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase pr-4">Usuario</th>
                        <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase pr-4">Plan</th>
                        <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase pr-4">Consumido</th>
                        <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase">Transacciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_consumers.map((user, idx) => (
                        <tr key={user.user_id} className="border-b border-blueGray-50 hover:bg-blueGray-50 transition-colors">
                          <td className="py-2 pr-4 text-blueGray-400 font-bold text-xs">{idx + 1}</td>
                          <td className="py-2 pr-4">
                            <p className="font-semibold text-blueGray-700 text-xs">{user.user_name}</p>
                            <p className="text-blueGray-400 text-xs">{user.user_email}</p>
                          </td>
                          <td className="py-2 pr-4">
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-600">
                              {user.plan_name ?? "Sin plan"}
                            </span>
                          </td>
                          <td className="py-2 pr-4">
                            <p className="font-bold text-orange-500 text-sm">
                              {Number(user.total_consumed).toLocaleString()}
                            </p>
                            <ProgressBar value={Number(user.total_consumed)} max={maxConsumed} colorClass="bg-orange-300" />
                          </td>
                          <td className="py-2 text-blueGray-600 text-xs">{user.transaction_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Consumo por proyecto */}
          <div className="px-4 mb-6">
            <div className="bg-white rounded-lg shadow-md p-5">
              <h3 className="text-sm font-bold text-blueGray-600 mb-4 flex items-center gap-2">
                <i className="fas fa-folder text-lightBlue-400"></i>
                Consumo por proyecto ({period} días)
              </h3>
              {!data.consumption_by_project || data.consumption_by_project.length === 0 ? (
                <p className="text-xs text-blueGray-400 text-center py-4">Sin consumo en el período</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-blueGray-100">
                        <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase pr-4">#</th>
                        <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase pr-4">Proyecto</th>
                        <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase pr-4">Consumido</th>
                        <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase pr-4">% del total</th>
                        <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase pr-4">Usuarios</th>
                        <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase pr-4">Transacciones</th>
                        <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.consumption_by_project.map((project, idx) => {
                        const maxProject = Math.max(...data.consumption_by_project.map((p) => p.total_consumed));
                        return (
                          <tr key={project.project_code} className="border-b border-blueGray-50 hover:bg-blueGray-50 transition-colors">
                            <td className="py-2 pr-4 text-blueGray-400 font-bold text-xs">{idx + 1}</td>
                            <td className="py-2 pr-4">
                              <p className="font-semibold text-blueGray-700 text-xs">{project.project_name}</p>
                              {project.project_code !== "sin_proyecto" && (
                                <p className="text-blueGray-400 text-xs font-mono">{project.project_code}</p>
                              )}
                            </td>
                            <td className="py-2 pr-4">
                              <p className="font-bold text-lightBlue-500 text-sm">
                                {project.total_consumed.toLocaleString()}
                              </p>
                              <ProgressBar value={project.total_consumed} max={maxProject} colorClass="bg-lightBlue-400" />
                            </td>
                            <td className="py-2 pr-4">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                                project.percentage >= 50
                                  ? "bg-orange-100 text-orange-600"
                                  : project.percentage >= 20
                                  ? "bg-yellow-100 text-yellow-700"
                                  : "bg-blueGray-100 text-blueGray-600"
                              }`}>
                                {project.percentage}%
                              </span>
                            </td>
                            <td className="py-2 pr-4 text-blueGray-600 text-xs">{project.active_users}</td>
                            <td className="py-2 pr-4 text-blueGray-600 text-xs">{project.transaction_count}</td>
                            <td className="py-2">
                              <button
                                onClick={() => setSelectedProject(project)}
                                className="text-xs text-lightBlue-500 hover:text-lightBlue-700 font-semibold flex items-center gap-1 transition-colors"
                                title="Ver usuarios del proyecto"
                              >
                                <i className="fas fa-users text-xs"></i>
                                Ver usuarios
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Usuarios en alerta */}
          {data.users_near_threshold?.length > 0 && (
            <div className="px-4 mb-6">
              <div className="bg-white rounded-lg shadow-md p-5 border-l-4 border-red-400">
                <h3 className="text-sm font-bold text-blueGray-600 mb-4 flex items-center gap-2">
                  <i className="fas fa-exclamation-triangle text-red-400"></i>
                  Usuarios cerca del límite (&lt; 20 % restante)
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-blueGray-100">
                        <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase pr-4">Usuario</th>
                        <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase pr-4">Plan</th>
                        <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase pr-4">Balance</th>
                        <th className="pb-2 text-xs font-bold text-blueGray-400 uppercase">% Restante</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.users_near_threshold.map((user) => (
                        <tr key={user.user_id} className="border-b border-blueGray-50 hover:bg-red-50 transition-colors">
                          <td className="py-2 pr-4">
                            <p className="font-semibold text-blueGray-700 text-xs">{user.user_name}</p>
                            <p className="text-blueGray-400 text-xs">{user.user_email}</p>
                          </td>
                          <td className="py-2 pr-4">
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-600">
                              {user.plan_name ?? "Sin plan"}
                            </span>
                          </td>
                          <td className="py-2 pr-4">
                            <span className="font-bold text-blueGray-700 text-xs">
                              {user.current_balance} / {user.monthly_credits}
                            </span>
                          </td>
                          <td className="py-2">
                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                                user.percentage_remaining <= 5
                                  ? "bg-red-100 text-red-600"
                                  : "bg-orange-100 text-orange-600"
                              }`}
                            >
                              {user.percentage_remaining}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {selectedProject && (
        <ProjectUsersModal
          projectCode={selectedProject.project_code}
          projectName={selectedProject.project_name}
          period={period}
          onClose={() => setSelectedProject(null)}
        />
      )}
    </>
  );
}
